/* eslint-disable indent */
import { SwipeableDrawer, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ChevronRight, ChevronUp } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  ContentContainer,
  WidgetContainer, StyledWidgetButtonDrawer
} from "./WidgetDrawer.styles";
import {
  CardUserGoals,
  KeepLearningWidget,
  PreviewWidget
  // NewsCard
} from "../../components";
import { AgendaWidget } from "../../components/AgendaWidget";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { UserProgressWidget } from "../../components/UserProgressWidget/UserProgressWidget";
import { useAuthStore } from "../../stores";
import { CardGoals } from "../CardGoals/CardGoals.component";
import { CardRanking } from "../CardRanking/CardRanking.component";
import { CardUserCampaigns } from "../CardUserCampaigns";

export function WidgetDrawer() {
  const { showTutorialWidget }: { showTutorialWidget: boolean } = useOutletContext();
  const [isOpened, setIsOpened] = React.useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  const isLoading = useAuthStore(state => state.isLoading);
  const isAuthenticated = useAuthStore(state => Boolean(state.userData && state.userInfo));
  const isLoginComplete = !isLoading && isAuthenticated;

  const widgetsAreEnabled =
        smartConfiguration?.leaderboard === true
        || smartConfiguration?.widgetBadge === true
        || smartConfiguration?.widgetContinueLearning === true
        || smartConfiguration?.widgetCalendar === true
        || smartConfiguration?.widgetEditorial === true;

  React.useEffect(() => {
    setIsOpened(showTutorialWidget);
  }, [showTutorialWidget]);

  if (!widgetsAreEnabled ) return null;
  return  (
    <> 
      <StyledWidgetButtonDrawer
        as="button"
        id="widget-toggle-button"
        data-testid="widget-toggle-open"
        aria-label={ t("widget_toggle_aria_label") }
        type="button"
        tabIndex={ !isOpened ? 0 : -1 }
        aria-expanded={ isOpened }
        aria-controls="widget-drawer"
        aria-haspopup="dialog"
        onClick={ () => {
          setIsOpened(true);
        }
        }
        style={
          !isOpened ? {
            position: "fixed",
            top: "20rem",
            right: "1rem",
            transform: "rotate(-90deg) translateX(100%)",
            transformOrigin: "right center",
            borderRadius: "0.25rem 0.25rem 0 0"
          } : {
            display: "none"
        } }
      >
        <ChevronUp
          size={ "1rem" }
          color={ theme.customColors.textPrimaryCta }
        />
        <Typography fontSize="0.875rem" color={ theme.customColors.textPrimaryCta }>
          { t("tutorial-step-3-title") }
        </Typography>
      </StyledWidgetButtonDrawer>
      <SwipeableDrawer
        tabIndex={ -1 }
        open={ isOpened }
        anchor="right"
        onClose={ () => {
          setIsOpened(false);
        } }
        onOpen={ () => {
          setIsOpened(true);
        } }
        PaperProps={ {
          id: "widget-drawer",
          role: "dialog",
          "aria-modal": true,
          sx: {
            background: "transparent",
            boxShadow: "none",
            height: "calc(100% - 6rem)",
            width: "27rem",
            overflowY: "visible",
            top: isMobile ? "8.8rem" : " 5rem",
            [theme.breakpoints.down("sm")]: {
              maxWidth: "100%",
              padding:"0.875rem",
              top:"3rem"
            },

            ".MuiPaper-root": {
              overflowX: "hidden",
              overflowY: "auto"
            }

          }
        } }
        slotProps={ { backdrop: { classes: { root: "backdrop-widget" } } } }
        // Durante il tutorial il drawer va sopra il backdrop del tour (1000001).
        sx={ { position: "relative", zIndex: showTutorialWidget ? 1000002 : 1400 } }
      >
        { /*
                    //keyremount force the widgets to re-render so they
                        // trigger "fetching on mount" for the queries
                     */ }
                    
        <ContentContainer tabIndex={ -1 }   style={ { position: "relative", marginTop: "2.5rem" } }>
          <ButtonDigited
            aria-label={ t("close_drawer") }
            typology="primary"
            noMarginOnFocus={ true }
            onClick={ () => {
              setIsOpened(false);
            } }
            sx={ {
              justifyContent:"center",
              borderRadius: "0.5rem",
              position: "fixed",
              padding: "0.25rem 0.5rem",
              width: "1.25rem",
              height: "1.25rem",
              marginTop: "-3.875rem",
              [theme.breakpoints.down("sm")]: {
                position: "sticky",
                top: 0,
                marginTop: 0,
                marginBottom: "1.5rem",
                zIndex: 1
              }
            } }
          >
            <ChevronRight
              size={ "1rem" }
              color={ theme.customColors.textPrimaryCta }
            />
          </ButtonDigited>
          <WidgetContainer >
            { smartConfiguration?.widgetEditorial && <PreviewWidget enabled={ isOpened && isLoginComplete }/> }
            { smartConfiguration?.widgetCalendar && <AgendaWidget enabled={ isOpened && isLoginComplete }/> }
            { smartConfiguration?.progressWidget && <UserProgressWidget enabled={ isOpened && isLoginComplete }/> }
            { smartConfiguration?.widgetContinueLearning && <KeepLearningWidget enabled={ isOpened && isLoginComplete } setIsOpened={ setIsOpened }/> }
            <CardRanking isUserPage={ false } enabled={ isOpened && isLoginComplete }/>
            { /*{ smartConfiguration?.widgetLeaderboard && <CardRanking isUserPage={ false } /> }*/ }
            { smartConfiguration?.widgetBadge && <CardGoals enabled={ isOpened && isLoginComplete }/> }
            { /* <NewsCard { ...NewsCardVariants.Default }/> */ }
            <CardUserGoals color={ theme.customColors.textWidget } isProfilePage={ false } enabled={ isOpened && isLoginComplete }/>
            <CardUserCampaigns enabled={ isOpened && isLoginComplete }/>
          </WidgetContainer>
        </ContentContainer>
      </SwipeableDrawer>
    </>
  ) ;
}
